/**
 * buildTrendTooltipFormatter
 *
 * A reusable ECharts tooltip formatter factory for trend charts that display
 * Actual / Forecast / KPI series with capsule bars and optional project breakdowns.
 *
 * Used by:
 * - PortfolioOverview (Revenue/Profit Trend)
 * - Studios (Revenue/Profit Trend)
 *
 * USAGE:
 *   import { buildTrendTooltipFormatter } from '@sg/utils/buildTrendTooltipFormatter';
 *
 *   // Inside setOtherConfigFunc:
 *   buildTrendTooltipFormatter(opt, {
 *     actualName: formatMessage({ id: 'dbs_charts_legend_actual' }),
 *     forecastName: formatMessage({ id: 'dbs_charts_legend_forecast' }),
 *     kpiName: formatMessage({ id: 'dbs_charts_legend_kpi' }),
 *     mathFormat,
 *     dealTypeValue,
 *     defaultCoverImage: GameDefault,
 *   });
 */

import {
  buildTooltipCapsuleBar,
  buildTooltipCapsulePlaceholder,
} from '@sg/components/CapsuleBar/TooltipCapsuleBar';
import GameDefault from '@/pages/home/assets/img/common/game-default.svg';

export interface ITrendTooltipOptions {
  /** Localized name for the "Actual" series */
  actualName: string;
  /** Localized name for the "Forecast" series */
  forecastName: string;
  /** Localized name for the "KPI" series */
  kpiName: string;
  /** Active card type: 'Revenue' or 'Profit' — used for the summary row label */
  activeCard: string;
  /** Number formatting function (e.g., 1234567 → "1.2M") */
  mathFormat: (value: number) => string;
  /** Value type formatting function (e.g., percent, num) */
  dealTypeValue: (type: string, value: number) => string;
}

/**
 * Installs a custom tooltip formatter on the given ECharts option object.
 * The formatter handles Actual/Forecast/KPI series with:
 * - 3-column grid layout (name | capsule+% | values)
 * - Capsule bar visualization for KPI achievement
 * - Project breakdown rows (up to 4 projects)
 * - Automatic mixed/positive capsule type detection
 * - Fallback to original formatter for non-target series
 *
 * @param opt - The ECharts option object (mutated in place)
 * @param options - Configuration for series names, formatters, etc.
 */
export function buildTrendTooltipFormatter(
  opt: any,
  options: ITrendTooltipOptions,
): void {
  const {
    actualName,
    forecastName,
    kpiName,
    activeCard,
    mathFormat: formatValue,
    dealTypeValue: formatTypeValue,
  } = options;

  // Check if chart has Actual or Forecast series
  const hasActualOrForecast = opt.series?.some((series: any) => {
    return series.name === actualName || series.name === forecastName;
  });

  if (!hasActualOrForecast || !opt.tooltip) return;

  const originalFormatter = opt.tooltip.formatter;

  /**
   * Calculate KPI completion with sign-aware business rules:
   * 1) KPI > 0, Value > 0: value / KPI
   * 2) KPI < 0, Value < 0: ((2 * KPI) - Value) / KPI
   * 3) KPI > 0, Value < 0: value / KPI
   * 4) KPI < 0, Value > 0: 1 + (Value - KPI) / |KPI|
   *
   * Also handles zero KPI safely:
   * - 0 / 0 => 0%
   * - non-zero / 0 => invalid (null)
   */
  const calcKpiPercent = (
    value: number | null | undefined,
    kpi: number | null | undefined,
  ): number | null => {
    if (value === null || value === undefined || kpi === null || kpi === undefined) {
      return null;
    }

    if (kpi === 0) {
      return value === 0 ? 0 : null;
    }

    // KPI >= 0 follows direct ratio, including value < 0.
    if (kpi > 0) {
      return Math.round((value / kpi) * 100);
    }

    // KPI < 0 cases
    if (value < 0) {
      // Both negative: use sign-aware completion conversion.
      return Math.round((((2 * kpi) - value) / kpi) * 100);
    }

    // KPI negative, value positive.
    return Math.round((1 + ((value - kpi) / Math.abs(kpi))) * 100);
  };

  opt.tooltip.formatter = (params: any[]) => {
    // Guard: skip when all series values are empty (null/undefined/NaN).
    const allSeriesEmpty = params.every((item: any) => {
      const rawValue = item?.data?.tooltipValue ?? item?.value;
      return rawValue === null || rawValue === undefined ||
        (typeof rawValue === 'number' && Number.isNaN(rawValue));
    });
    if (allSeriesEmpty) return '';

    // Check if this data point has Actual or Forecast series
    const hasTargetSeries = params.some((item: any) => {
      return item.seriesName === actualName || item.seriesName === forecastName;
    });

    if (hasTargetSeries) {
      // Extract all series data
      const actualItem = params.find((p: any) => p.seriesName === actualName);
      const forecastItem = params.find((p: any) => p.seriesName === forecastName);
      const kpiItem = params.find((p: any) => p.seriesName === kpiName);

      const actualRawValue = actualItem?.data?.tooltipValue ?? actualItem?.value;
      const forecastRawValue = forecastItem?.data?.tooltipValue ?? forecastItem?.value;
      const kpiRawValue = kpiItem?.data?.tooltipValue ?? kpiItem?.value;

      const hasActualValue = actualRawValue !== null && actualRawValue !== undefined;
      const hasForecastValue = forecastRawValue !== null && forecastRawValue !== undefined;
      const hasKpiValue = kpiRawValue !== null && kpiRawValue !== undefined;

      // If Actual / Forecast / KPI are all empty, hide tooltip entirely.
      // This prevents showing tooltip headers on months that have no data.
      if (!hasActualValue && !hasForecastValue && !hasKpiValue) {
        return '';
      }

      // Determine primary series: use Actual if it has data, otherwise Forecast
      const useActual = hasActualValue;
      const primaryName = useActual ? actualName : forecastName;
      const primaryItem = useActual ? actualItem : forecastItem;
      const primaryRawValue = useActual ? actualRawValue : forecastRawValue;
      const hasPrimaryValue = useActual ? hasActualValue : hasForecastValue;

      // Format values
      const primaryFormatted = hasPrimaryValue ? formatValue(primaryRawValue) : '-';
      const kpiFormatted = hasKpiValue ? formatValue(kpiRawValue) : '-';

      // Calculate % of KPI
      const kpiPercent = (hasPrimaryValue && hasKpiValue)
        ? calcKpiPercent(primaryRawValue, kpiRawValue)
        : null;
      const kpiPercentDisplay = kpiPercent !== null ? `${kpiPercent}%` : '-';

      // Use fixed color based on primary series (avoids inheriting opacity from forecast bar markers)
      const capsuleColor = useActual ? '#5EC8F1' : '#7F6FF1';

      // Summary row label — always show "YTM Gross Revenue" or "YTM Profit"
      const dataName = activeCard === 'Profit' ? 'YTM Profit' : 'YTM Gross Revenue';

      // Build custom markers with full-opacity colors (avoids inheriting bar opacity from ECharts markers)
      const primaryMarker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${capsuleColor};"></span>`;
      // const kpiMarker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:#FC8B00;"></span>`;

      // Build header cells
      const headerCells = `
        <span>${params[0]?.name}</span>
        <span>% of KPI</span>
        <span style="text-align:right; white-space:nowrap;">
          ${primaryMarker} ${primaryName}
          <span style="margin:0 4px;">/</span>
          ${kpiItem?.marker || ''} ${kpiName}
        </span>`;

      // Get projects and slice to top 4 (the ones actually displayed)
      const allProjects = (actualItem?.data?.projects || forecastItem?.data?.projects || []) as any[];
      const projects = allProjects.slice(0, 4);

      // Determine capsule type based on summary + visible projects that have valid capsules
      // A valid capsule requires both value and KPI to be present
      // If any valid capsule has a negative value or KPI, all capsules use mixed layout
      const hasNegativeSummary = (hasPrimaryValue && hasKpiValue) &&
        (primaryRawValue < 0 || kpiRawValue < 0);
      const hasNegativeProject = projects.some((proj: any) => {
        const val = proj.actual ?? proj.forecast ?? null;
        const kpi = proj.kpi ?? null;
        const hasVal = val !== null && val !== undefined;
        const hasKpi = kpi !== null && kpi !== undefined;
        // Only consider projects that would render a capsule (both value and KPI present)
        if (!hasVal || !hasKpi) return false;
        return val < 0 || kpi < 0;
      });
      const capsuleType: 'positive' | 'mixed' =
        (hasNegativeSummary || hasNegativeProject) ? 'mixed' : 'positive';

      // Build summary row with capsule bar
      const capsuleBarHtml = (hasPrimaryValue && hasKpiValue)
        ? buildTooltipCapsuleBar({
            value: primaryRawValue,
            kpiValue: kpiRawValue,
            barColor: capsuleColor,
            type: capsuleType,
          })
        : buildTooltipCapsulePlaceholder({
            type: capsuleType,
          });

      const summaryRow = (hasPrimaryValue || hasKpiValue) ? `
        <span style="color:#EBECF1; white-space:nowrap;">${dataName}</span>
        <div style="display:flex; align-items:center; gap:6px;">
          ${capsuleBarHtml}
          <span style="color:${capsuleColor}; white-space:nowrap; font-weight:700; font-size:14px;">${kpiPercentDisplay}</span>
        </div>
        <span style="text-align:right; white-space:nowrap; font-weight:700; font-size:14px;">
          ${primaryFormatted} / ${kpiFormatted}
        </span>` : '';

      // Escape CSS-sensitive chars for background-image url('...') to avoid
      // malformed inline styles when backend URLs contain quotes, backslashes,
      // or closing parentheses.
      const escapeCssUrl = (url: string): string =>
        url.replace(/["\\)]/g, (match: string) => `\\${match}`);

      // Build project breakdown rows
      const projectRows = projects.map((proj: any) => {
        const projValue = proj.actual ?? proj.forecast ?? null;
        const projKpi = proj.kpi ?? null;
        const hasProjValue = projValue !== null && projValue !== undefined;
        const hasProjKpi = projKpi !== null && projKpi !== undefined;
        const projKpiPercent = (hasProjValue && hasProjKpi)
          ? calcKpiPercent(projValue, projKpi)
          : null;
        const projKpiPercentDisplay = projKpiPercent !== null ? `${projKpiPercent}%` : '-';
        const projValueFormatted = hasProjValue ? formatValue(projValue) : '-';
        const projKpiFormatted = hasProjKpi ? formatValue(projKpi) : '-';

        const projCapsuleHtml = (hasProjValue && hasProjKpi)
          ? buildTooltipCapsuleBar({
              value: projValue,
              kpiValue: projKpi,
              barColor: capsuleColor,
              type: capsuleType,
            })
          : buildTooltipCapsulePlaceholder({
              type: capsuleType,
            });

        const coverSrc = proj.cover || '';
        const escapedDefaultCover = escapeCssUrl(GameDefault);
        const escapedProjectCover = coverSrc ? escapeCssUrl(coverSrc) : '';
        // span is used here instead of img to render image because CSS background painting is generally more stable than remounting <img> nodes on every formatter pass.
        // to prevent the image blinking when hovering and moving the mouse.
        const coverHtml = `<span style="
          display:inline-block;
          width:20px;
          height:20px;
          margin-right:6px;
          vertical-align:middle;
          border-radius:4px;
          background-color:#5A5E6B;
          background-repeat:no-repeat,no-repeat;
          background-position:center center,center center;
          background-size:cover,cover;
          background-image:${escapedProjectCover
            ? `url('${escapedProjectCover}'), url('${escapedDefaultCover}')`
            : `url('${escapedDefaultCover}')`};
        "></span>`;

        return `
          <span style="display:flex; align-items:center; white-space:nowrap;">
            ${coverHtml}<span style="color:#EBECF1;">${proj.project_name || ''}</span>
          </span>
          <div style="display:flex; align-items:center; gap:6px;">
            ${projCapsuleHtml}
            <span style="color:${capsuleColor}; white-space:nowrap; font-weight:700; font-size:14px;">${projKpiPercentDisplay}</span>
          </div>
          <span style="text-align:right; white-space:nowrap; font-weight:700; font-size:14px;">
            ${projValueFormatted} / ${projKpiFormatted}
          </span>`;
      }).join('');

      // Build other series rows — exclude the primary series and KPI
      const otherRows = params
        .filter((item: any) => item.seriesName !== primaryName && item.seriesName !== kpiName)
        .map((item: any) => {
          const itemData = item?.data || {};
          if (itemData.render) {
            return itemData.render(item);
          }

          let displayValue = itemData.tooltipValue ?? item.value;

          // If Forecast equals Actual, skip it (it's just a continuation point)
          if (item.seriesName === forecastName && hasActualValue) {
            if (displayValue !== null && displayValue !== undefined &&
                actualRawValue === displayValue) {
              return '';
            }
          }

          const hasDisplayValue = displayValue !== null && displayValue !== undefined;
          const formattedValue = hasDisplayValue
            ? (itemData.valueType
              ? formatTypeValue(itemData.valueType, displayValue)
              : formatValue(displayValue))
            : '-';
          const safeFormattedValue = formattedValue ?? '-';

          if (safeFormattedValue === '-' || safeFormattedValue === '0.0M') {
            return '';
          }

          return `
            <span>${item.marker} <span>${item.seriesName}</span></span>
            <span></span>
            <span style="text-align:right; font-weight:600; color:${itemData.textColor || ''}">${safeFormattedValue}</span>`;
        }).join('');

      // Divider line between summary row and project rows
      const projectDivider = projects.length > 0
        ? `<div style="grid-column:1/-1; height:1px; background:rgba(216,218,228,0.3);"></div>`
        : '';

      return `<div style="display:grid; grid-template-columns:auto 1fr auto; align-items:center; column-gap:20px; row-gap:10px; font-size:12px; color:#D8DAE4;">
        ${headerCells}
        ${summaryRow}
        ${projectDivider}
        ${projectRows}
        ${otherRows}
      </div>`;
    }

    // Fallback: use original formatter for other series
    if (originalFormatter && typeof originalFormatter === 'function') {
      return originalFormatter(params);
    }
    return '';
  };
}

export default buildTrendTooltipFormatter;
